import { useAuth } from '@context/authContext';
import {
    cancelNotificationStatusUser,
    getNotificationStatusUser,
    signUpNotificationStatusUser,
} from '@server/requests/notification';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

type UseSignUpProps = {
    productId: string;
    variantId?: string;
};

export default function useSignUp({ productId, variantId }: UseSignUpProps) {
    const { auth, showSignup } = useAuth();
    const queryClient = useQueryClient();

    const { data: active, isLoading } = useQuery({
        queryKey: ['notification', productId],
        queryFn: async () => {
            if (!variantId) return false;
            const response = await getNotificationStatusUser({
                token: auth?.token ?? '',
                productId,
            });
            if (response.getNotificationStatusUser.isError) return false;
            return response.getNotificationStatusUser.data?.variantIdsWithNotifications?.includes(
                variantId,
            );
        },
        enabled: Boolean(auth?.token),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const { mutate: signup, isPending: signingUp } = useMutation({
        mutationFn: () =>
            signUpNotificationStatusUser({
                token: auth?.token ?? '',
                variantId: variantId ?? '',
                productId,
            }),
        onSuccess: (response) => {
            if (!response.signUpNotificationStatusUser.isError) {
                queryClient.setQueryData(['notification', productId], 'WUT');
            }
        },
    });
    const { mutate: signoff, isPending: signingOff } = useMutation({
        mutationFn: () =>
            cancelNotificationStatusUser({
                token: auth?.token ?? '',
                variantId: variantId ?? '',
                productId,
            }),
        onSuccess: (response) => {
            if (!response.cancelNotificationStatusUser.isError) {
                queryClient.setQueryData(['notification', productId], false);
            }
        },
    });

    const toggleNotification = useCallback(() => {
        if (auth?.user) {
            if (active) {
                signoff();
            } else {
                signup();
            }
        } else {
            showSignup();
        }
    }, [auth, active, signup, signoff, showSignup]);

    const loading = isLoading || signingUp || signingOff;

    return { active, toggleNotification, loading };
}
